import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Provider, useDispatch, useSelector} from 'react-redux';
import { useHistory } from 'react-router-dom';
import ReactDOM from "react-dom";
import mapboxgl from "mapbox-gl";

import store, { devicesSelectedActions } from './store';
import { t } from "./common/LocalizationProvider";
import EditCollectionViewDevice from "./EditCollectionViewDevice";
import {map} from "./map/Map";
import StatusView from "./map/StatusView";
import ButtonsHistory from "./shared/ButtonsHistory";
import SearshDevices from "./details/SearshDevices";

import {makeStyles, ThemeProvider} from '@material-ui/core/styles';
import {Paper, Grid, useMediaQuery, useTheme,} from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import ReplayIcon from '@material-ui/icons/Replay';
import ListAltIcon from '@material-ui/icons/ListAlt';
import SignalCellularAltIcon from "@material-ui/icons/SignalCellularAlt";
import Tooltip from "@material-ui/core/Tooltip";
import {formatterTimeDuration} from "./common/formatter";
import {cyberStyles} from "./cssShared/cyberTheme";

const useStyles = makeStyles((theme) => ({
  ...cyberStyles(theme),
  root: {
    height: '100%',
    position: 'relative',
  },
  listItem: {
    '&:has($moreIcon:hover)': {
      backgroundColor: 'transparent !important',
    },
    '&:has($signalIcon:hover)': {
      backgroundColor: 'transparent !important',
    },
  },
  margin: {
    textTransform: 'none',
  },
  icon: {
    width: '25px',
    height: '25px',
    filter: 'brightness(0) invert(1)',
  },
  configue: {
    color: 'red',
  },
  secondaryActionContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  desktopFooter: {
    position: 'fixed',
    bottom: theme.spacing(0),
    left: 0,
    zIndex: 12,
    width: theme.dimensions.drawerWidthDesktop,
  },
  sidebarExpanded: {
    [theme.breakpoints.up('sm')]: {
      height: 'calc(100vh - 64px)',
    },
  },
  middleDesktopExpanded: {
    maxHeight: 'calc(100vh - 200px)',
  },
}));

const getStatusColor = (status) => {
  switch (status) {
    case 'online':
      return 'green';
    case 'offline':
      return 'gray';
    case 'slowing':
      return 'yellow';
    default:
      return 'red';
  }
};

function smoothFlyTo(googleMap, targetLatLng, targetZoom, step = 0.5, delay = 50) {
  return new Promise((resolve) => {
    const currentZoom = googleMap?.getZoom?.();
    if (currentZoom == null) {
      resolve();
      return;
    }

    // Start panning smoothly
    googleMap.panTo(targetLatLng);
    const zoomSteps = [];

    // Create zoom steps between current and target
    if (currentZoom < targetZoom) {
      for (let z = currentZoom + step; z <= targetZoom; z += step) {
        zoomSteps.push(Number(z.toFixed(1)));
      }
    } else {
      for (let z = currentZoom - step; z >= targetZoom; z -= step) {
        zoomSteps.push(Number(z.toFixed(1)));
      }
    }

    let i = 0;
    const interval = setInterval(() => {
      if (i >= zoomSteps.length) {
        clearInterval(interval);
        resolve(); // ✅ Resolve the promise here
        return;
      }
      googleMap.setZoom(zoomSteps[i]);
      i++;
    }, delay);
  });
}


const DevicesList = ({ googleMap, phoneDeviceList, phoneDevicesFiltred, phoneSortBy }) => {

  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

  const positions = useSelector(state => Object.values(state.positions));
  const mapType = useSelector(state => state.session.user && state.session.user.map)

  const infoWindowRef = useRef(null);
  const clickListenerRef = useRef(null)
  const [deviceList, setDeviceList] = useState(true);
  const [devicesFiltred, setDevicesFiltred] = useState( phoneDevicesFiltred || []);
  const [sortBy ,setSortBy] = useState(phoneSortBy || '');
  const [menuAnchor, setMenuAnchor] = useState(null);

  useEffect(() => {
    if (phoneDevicesFiltred)
      setDevicesFiltred(phoneDevicesFiltred);
    if (phoneSortBy)
      setSortBy(phoneSortBy)
  }, [phoneDevicesFiltred, phoneSortBy]);


  const handleClick = (id) => {
    history.push(`/position/`+id);
  };

  const onZoom = async (id) => {
    const position = positions[0]?.[id];
    if (mapType === 'googleMap') {
      if (position){
        if (!googleMap.googleMap || !googleMap.googleMapsApi) return;

        if (infoWindowRef.current){
          infoWindowRef.current.close();
          infoWindowRef.current = null;
        }
        if (clickListenerRef.current){
          googleMap.googleMapsApi.event.removeListener(clickListenerRef.current);
          clickListenerRef.current = null;
        }
        await smoothFlyTo(googleMap.googleMap, { lat: position.latitude, lng: position.longitude }, 15);

        const googleMapsApi = googleMap.googleMapsApi;
        const placeholder = document.createElement('div');
        ReactDOM.render(
            <Provider store={store}>
              <ThemeProvider theme={theme}>
                <StatusView deviceId={id} onShowDetails={positionId => history.push(`/position/${positionId}`)} history={history}/>
              </ThemeProvider>
            </Provider>,
            placeholder
        );
        const infoWindow = new googleMapsApi.InfoWindow({
          content: placeholder,
          position: { lat: position.latitude, lng: position.longitude }
        })
        infoWindow.open(googleMap.googleMap)
        infoWindowRef.current = infoWindow;
        clickListenerRef.current = googleMap.googleMap.addListener('click', () => {
          if (infoWindowRef.current){
            infoWindowRef.current.close();
            infoWindowRef.current = null;
          }
        })
      }
    }
    else {
      Array.from(map.getContainer().getElementsByClassName('mapboxgl-popup')).forEach(el => el.remove());
      if (position) {
        await new Promise((resolve) => {
          map.flyTo({
            center: [position.longitude, position.latitude],
            zoom: 15,
            essential: true // This ensures the animation runs
          });
          map.once('moveend', resolve);
        });
        const placeholder = document.createElement('div');
        ReactDOM.render(
            <Provider store={store}>
              <ThemeProvider theme={theme}>
                <StatusView deviceId={id} onShowDetails={positionId => history.push(`/position/${positionId}`)} history={history}/>
              </ThemeProvider>
            </Provider>,
            placeholder
        );
        new mapboxgl.Popup({
          offset: 25,
          anchor: 'top'
        })
            .setDOMContent(placeholder)
            .setLngLat([position.longitude, position.latitude])
            .addTo(map);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (clickListenerRef.current) {
        clickListenerRef.current.remove();
      }
    };
  }, []);

  const getColor = (id) => {
    if (positions[0]?.[id]) {
      if (positions[0]?.[id].attributes.gpsSgn < 3)
        return 'red';
      else if (positions[0]?.[id].attributes.gpsSgn >= 3 && positions[0]?.[id].attributes.gpsSgn < 5)
        return 'yellow'
      else
        return 'green'
    }
    else
      return 'blue';
  }

  const devicesSorted = useMemo(() => {
    const devices = [...devicesFiltred];
    if (sortBy === 'name') {
      devices.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'updated') {
      devices.sort((a, b) => new Date(b.lastUpdate) - new Date(a.lastUpdate));
    } else if (sortBy === 'createdAt') {
      devices.sort((a, b) => a.id - b.id);
    }
    return devices;

  }, [devicesFiltred, sortBy]);

  return (
      <div className={classes.root}>
        <div className={`${classes.sidebar} ${isDesktop && deviceList ? classes.sidebarExpanded : ''}`}>
          {!phoneDeviceList &&
              <SearshDevices
                  setDeviceList={setDeviceList}
                  deviceList={deviceList}
                  setDevicesFiltred={setDevicesFiltred}
                  setSortBy={setSortBy}
                  sortBy={sortBy}
              />}
          <div className={isDesktop ? `${classes.middleDesktop} ${deviceList ? classes.middleDesktopExpanded : ''}` : classes.middle}>
            {deviceList && (
                <List disablePadding>
                  {devicesSorted.map((item) => (
                      <Paper className={classes.deviceCard} key={item.id}>
                        <ListItem button className={classes.listItem} onClick={() => onZoom(item.id)}>
                          <ListItemAvatar>
                            <Avatar style={{"backgroundColor": getStatusColor(item.status)}}>
                              <img className={classes.icon} src={`images/icon/${item.category || 'default'}.svg`} alt=""/>
                            </Avatar>
                          </ListItemAvatar>

                          <ListItemText
                              primary={item.name}
                              secondary={item.uniqueId}
                              className={`${classes.textWrapper} ${item.configured !== "configured" ? classes.configue : ''}`}
                          />

                          <div className={classes.secondaryActionContainer}>
                            <Tooltip title={positions[0]?.[item.id]?.attributes?.gpsSgn || 0}>
                              <SignalCellularAltIcon
                                  className={classes.signalIcon}
                                  style={{color: getColor(item.id)}}
                              />
                            </Tooltip>
                            <IconButton
                                className={classes.moreIcon}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setMenuAnchor({ el: event.currentTarget, id: item.id })
                                }
                            }
                            >
                              <MoreVertIcon/>
                            </IconButton>
                          </div>
                        </ListItem>

                        <Grid container style={{ padding: '0 16px 8px 16px', alignItems: 'center' }}>
                          <Grid item xs>
                            <ListItemText
                                className={classes.statusTextContainer}
                                primary={t(item.status)}
                                secondary={formatterTimeDuration(positions[0][item.id], item)}
                                primaryTypographyProps={{
                                  style: { color: getStatusColor(item.status) }
                                }}
                            />
                          </Grid>

                          <Grid item>
                            <IconButton
                                size="medium"
                                className={classes.moreIcon}
                                color="primary"
                                onClick={() => {handleClick(item.positionId);}}
                            >
                              <ListAltIcon/>
                            </IconButton>

                            <IconButton
                                size="medium"
                                className={classes.moreIcon}
                                color="primary"
                                onClick={() => onZoom(item.id)}
                            >
                              <LocationOnIcon/>
                            </IconButton>

                            <IconButton
                                size="medium"
                                className={classes.moreIcon}
                                color="primary"
                                onClick={() => {
                                  dispatch(devicesSelectedActions.updateDevicesSelected([item.id]));
                                  history.push(`replay/${item.id}`);
                                }}
                            >
                              <ReplayIcon/>
                            </IconButton>
                          </Grid>

                        </Grid>
                      </Paper>
                  ))}
                </List>
            )}
          </div>
        </div>

        {isDesktop &&
            <div className={classes.desktopFooter}>
              <ButtonsHistory />
            </div>
        }
        <EditCollectionViewDevice editPath="/device" endpoint="devices" menuAnchor={menuAnchor} setMenuAnchor={setMenuAnchor}/>
      </div>
  );
}

export default React.memo(DevicesList);
